// @file Mobile app related state actions and mutations.
// * Note: we don't want to call postMessage directly here.
// * We want to instead set the state and let plugins/components respond and act
// * accordingly with native bridge.
import { Post } from 'types'
import { ActionContext, Module } from 'vuex'
import { RootState } from 'vuexstore/surface/types'
import { LinkType } from 'native_bridge/types'
import { postSurfaceState } from 'native_bridge/actions'
import postMessage from 'native_bridge/post_message'
import { circularSafeStringify } from 'bits/json_stringify'

interface NativeAppOpenLink {
  url: string
  linkType: LinkType
  target?: '_blank' | '_top' | '_self'
  contentCategory: string
  contentSubcategory: string
}

interface AppState {
  linkToOpen: NativeAppOpenLink | null
  // Reference: https://linear.app/padlet/issue/PRO-302/more-permanent-fix-for-drafts-native-component-covering-plus-button
  // the native component for draft posts may cover the bottom of the screen, preventing user from interacting with buttons at the bottom of shelf sections
  // => let the app decide how much padding is needed so the buttons are not covered by the native component
  shelfSectionBottomPaddingPx: number
}

type NativeActionContext = ActionContext<AppState, RootState>

const NativeApp: Module<AppState, RootState> = {
  namespaced: true,
  state: {
    linkToOpen: null,
    shelfSectionBottomPaddingPx: 0,
  },
  getters: {
    expandedPost: (state, getters, rootState, rootGetters): Post | undefined => {
      return rootGetters['postAction/expandedPost']
    },
    shelfSectionBottomPaddingPx: (state): number => state.shelfSectionBottomPaddingPx,
  },
  mutations: {
    NATIVE_APP_OPEN_LINK(state, nativeAppOpenLink: NativeAppOpenLink): void {
      state.linkToOpen = nativeAppOpenLink
    },
    NATIVE_APP_LINK_OPENED(state): void {
      state.linkToOpen = null
    },
    NATIVE_APP_UPDATE_SHELF_SECTION_BOTTOM_PADDING(state, { new_padding }: { new_padding: number }): void {
      state.shelfSectionBottomPaddingPx = new_padding
    },
  },
  actions: {
    openLink({ commit }, nativeAppOpenLink: NativeAppOpenLink): void {
      commit('NATIVE_APP_OPEN_LINK', nativeAppOpenLink)
    },
    clearOpenLink({ commit }): void {
      commit('NATIVE_APP_LINK_OPENED')
    },
    hideAllPanelsAndMenus({ dispatch, rootState }): void {
      dispatch('settings/hideSettingsPanel', null, { root: true })
      dispatch('share/resetSharePanel', null, { root: true })
      dispatch('hideRemakePanel', null, { root: true })
      dispatch('hideConvertLayoutPanel', null, { root: true })
      dispatch('postAction/endPostCopy', null, { root: true })
      dispatch('postAction/endPostTransfer', null, { root: true })
      if (rootState.postAction && rootState.postAction.postUnderActionCid) {
        dispatch('postAction/hidePostActionMenu', null, { root: true })
      }
      if (rootState.section && rootState.section.actionMenuSectionId) {
        dispatch('section/hideSectionActionMenu', null, { root: true })
      }
      if (rootState.comment && rootState.comment.actionMenuCommentId) {
        dispatch('comment/hideCommentActionMenu', null, { root: true })
      }
    },
    updateShelfSectionBottomPadding({ commit }, payload: { new_padding: number }): void {
      commit('NATIVE_APP_UPDATE_SHELF_SECTION_BOTTOM_PADDING', payload)
    },
    postSurfaceState({ rootGetters }): void {
      const plainObjectAppState = JSON.parse(
        circularSafeStringify({
          wall_sections: rootGetters['section/sortedSections'],
        }),
      )
      postMessage(
        postSurfaceState({
          ...plainObjectAppState,
        }),
      )
    },
  },
}

export default NativeApp
